import React, {
  FC, Fragment, SetStateAction, useEffect, useState,
} from 'react';
import { connect } from 'react-redux';
import _, { flatMap } from 'lodash';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Checkbox,
  Popover,
  IconButton,
  TextField,
  InputAdornment,
  ListItemButton,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  NotificationsNone as NotificationsNoneIcon,
  Notifications as NotificationsIcon,
  SubscriptionsOutlined as SubscriptionsNoneIcon,
  Subscriptions as SubscriptionsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ReactTooltip from 'react-tooltip';
import {
  ControlLabel,
} from '../styles';
import {
  IProps,
  IStateProps,
  IDispatchProps,
  IState,
} from './interfaces';
import {
  ActionButtonClass,
  Container,
  ImageContainer,
  Image,
  TeamsList,
  TeamCheckbox,
  Button,
  Header,
} from './styles';
import { IAppState } from '../../../../state/reducers';
import { selectIsUserExchangeVendor } from '../../../../state/user/userOrgRole';
import {
  toggleConferenceExpandedState,
  toggleAllTeamsSelection,
  toggleTeamSelection,
  clearTeamSelection,
  toggleConferenceAllExpandedState,
  selectTeamFilter,
  updateTeamFilter,
} from '../../../../state/exchange/searchParams';
import {
  updateSettings,
  saveSettings,
  queryNotificationSettings,
} from '../../../../state/exchange/notificationSettings';
import { IVaultSubscription } from '../../../../api/vault/subscriptions';
import { hideSubscriptions } from '../../../../utils/exchange';

// TODO: visual effect instead of no-render for notification icons when not subscribed

const ExchangeSearchParams: FC<IProps> = (props: IProps) => {
  const {
    conferences,
    isUserExchangeVendor,
    onConferenceToggle,
    onConferenceToggleAll,
    onAllTeamsSelection,
    subscription,
    onUpdateSettings,
    onSaveSettings,
    onQueryNotificationSettings,
    onTeamSelectionToggle,
    onTeamSelectionClear,
    filterText,
    setFilterText,
    sport,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
  const [allExpanded, setAllExpanded] = useState(false);
  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    onQueryNotificationSettings();
    ReactTooltip.rebuild();
  }, [
    onUpdateSettings,
    onSaveSettings,
  ]);

  const openTeamSelection = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterText('');
  };

  const closeTeamSelection = () => {
    setAnchorEl(null);
    setAllExpanded(false);
    onConferenceToggleAll(false);
  };

  const cleanList = (list: any[]) => list.filter((subscription) => subscription.notify || subscription.subscribe);

  const onToggleSubscription = ({
    teamId,
    teamCode,
    teams,
    isSubscribed,
  }: { teamId?: string, teamCode?: string, teams?: Array<any>, isSubscribed: boolean }) => {
    const { subscribedTeams } = subscription;

    let subscriptionUpdate: IVaultSubscription;

    if (teamCode) {
      // note: assume teamCode is supplied w/ teamId param
      const haveOne = subscribedTeams.find((team) => teamId === team.teamId);
      let subs = subscribedTeams;
      if (haveOne) {
        haveOne.subscribe = !isSubscribed;
      } else {
        subs = [...subs, {
          teamId,
          teamCode,
          notify: false,
          subscribe: true,
          exchangeQualifier: [],
        }];
      }

      subs = cleanList(subs);

      subscriptionUpdate = {
        ...subscription,
        subscribedTeams: subs,
      };
    } else if (teams) {
      const teamIds = teams.map((team: any) => [team.teamId, team.teamCode]);
      let subs = subscribedTeams;
      teamIds.forEach(([id, code]) => {
        // note: assume teamCode is supplied w/ teamId param
        const haveOne = subs.find((team) => id === team.teamId);
        if (haveOne) {
          haveOne.subscribe = !isSubscribed;
        } else {
          subs = [...subs, {
            teamId: id,
            teamCode: code,
            notify: false,
            subscribe: true,
            exchangeQualifier: [],
          }];
        }
      });

      subs = cleanList(subs);

      subscriptionUpdate = {
        ...subscription,
        subscribedTeams: subs,
      };
    }

    onUpdateSettings(subscriptionUpdate);
    onSaveSettings();
  };

  const onToggleNotification = ({
    teamId,
    teamCode,
    teams,
    isNotify,
  }: { teamId?: string, teamCode?: string, teams?: Array<any>, isNotify: boolean }) => {
    const { subscribedTeams } = subscription;

    let subscriptionUpdate: IVaultSubscription;

    if (teamId) {
      // note: assume teamCode is supplied w/ teamId param
      const haveOne = subscribedTeams.find((team) => teamId === team.teamId);
      let subs = subscribedTeams;
      if (haveOne) {
        haveOne.notify = !isNotify;
      } else {
        subs = [...subs, {
          teamId,
          teamCode,
          notify: true,
          subscribe: false,
          exchangeQualifier: [],
        }];
      }

      subs = cleanList(subs);

      subscriptionUpdate = {
        ...subscription,
        subscribedTeams: subs,
      };
    } else if (teams) {
      const teamIds: Array<Array<string>> = teams.map((team: any) => [team.teamId, team.teamCode]);
      let subs = subscribedTeams;
      teamIds.forEach(([id, code]) => {
        const haveOne = subs.find((team) => id === team.teamId);
        if (haveOne) {
          haveOne.notify = !isNotify;
        } else {
          subs = [...subs, {
            teamId: id,
            teamCode: code,
            notify: true,
            subscribe: false,
            exchangeQualifier: [],
          }];
        }
      });

      subs = cleanList(subs);

      subscriptionUpdate = {
        ...subscription,
        subscribedTeams: subs,
      };
    }

    onUpdateSettings(subscriptionUpdate);
    onSaveSettings();
  };

  const renderTeamOptions = () => {
    const { subscribedTeams } = subscription;

    // TODO: clean up once conference data loaded in from vault/idm api
    const mappedConferences = conferences.map((conference) => {
      let isSubscribed = true;
      let isNotify = true;

      const teams = conference.teams.map((team) => {
        const subscribed = subscribedTeams.find((_team) => ((team.teamId === _team.teamId) && (_team.subscribe == null || _team.subscribe)));
        const notify = subscribedTeams.find((_team) => ((team.teamId === _team.teamId) && _team.notify));

        if (!subscribed) {
          isSubscribed = false;
        }
        if (!notify) {
          isNotify = false;
        }

        return {
          ...team,
          isSubscribed: !!subscribed,
          isNotify: !!notify,
        };
      });

      const filteredTeams = teams.filter((team) => filterText === '' || team.teamName.toLowerCase().includes(filterText.toLowerCase()));

      return {
        ...conference,
        teams: filteredTeams,
        isSubscribed,
        isNotify,
      };
    });

    return (
      <TeamsList
        disablePadding
        dense
      >
        {mappedConferences.map(({
          conferenceName,
          conferenceImageFile,
          teams,
          isExpanded,
          isAllTeamsSelected,
          isSubscribed,
          isNotify,
        }, conferenceIndex) => (
          <Fragment
            key={conferenceName}
          >
            {(teams != null && teams.length > 0) && (
              <ListItem
                data-auto={conferenceName}
                data-tip="Conference"
                secondaryAction={(
                  <>
                    {isExpanded && !isUserExchangeVendor && !hideSubscriptions(sport) && (
                      <>
                        <IconButton
                          edge="end"
                          aria-label="Toggle Subscriptions"
                          className={`${ActionButtonClass} ${isSubscribed ? 'active' : ''}`}
                          data-tip={`${isSubscribed ? 'Remove' : 'Add'} Subscriptions`}
                          data-for="team-select-tooltip"
                          onClick={() => onToggleSubscription({ teams, isSubscribed })}
                          size="large"
                        >
                          {isSubscribed
                            ? <SubscriptionsIcon />
                            : <SubscriptionsNoneIcon />}
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="Toggle Notifications"
                          className={`${ActionButtonClass} ${isNotify ? 'active' : ''}`}
                          data-tip={`${isNotify ? 'Remove' : 'Add'} Notifications`}
                          data-for="team-select-tooltip"
                          onClick={() => onToggleNotification({ teams, isNotify })}
                          size="large"
                        >
                          {isNotify
                            ? <NotificationsIcon />
                            : <NotificationsNoneIcon />}
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      edge="end"
                      aria-label="Toggle Conferences"
                      onClick={() => onConferenceToggle(conferenceIndex)}
                      size="large"
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </>
                )}
                disablePadding
              >
                <ListItemButton onClick={() => onConferenceToggle(conferenceIndex)}>
                  <Checkbox
                    color="secondary"
                    edge="start"
                    checked={isAllTeamsSelected}
                    tabIndex={-1}
                    disableRipple
                    onChange={() => onAllTeamsSelection(conferenceIndex)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ImageContainer>
                    <Image src={conferenceImageFile} />
                  </ImageContainer>
                  <ListItemText
                    primary={conferenceName}
                  />
                </ListItemButton>
              </ListItem>
            )}
            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
            >
              <List
                component="div"
                disablePadding
                dense
              >
                {teams.map(({
                  teamId,
                  teamName,
                  teamCode,
                  teamImageFile,
                  conferenceName,
                  isSelected,
                  isSubscribed, // eslint-disable-line
                  isNotify, // eslint-disable-line
                }, teamIndex) => (
                  <ListItem
                    data-auto={teamName}
                    data-tip="Team"
                    key={teamName}
                    secondaryAction={!isUserExchangeVendor && !hideSubscriptions(sport) && (
                      <>
                        <IconButton
                          sx={{ padding: '10px' }}
                          edge="end"
                          aria-label="Toggle Subscription"
                          className={`${ActionButtonClass} ${isSubscribed ? 'active' : ''}`}
                          data-tip={`${isSubscribed ? 'Remove' : 'Add'} Subscription`}
                          data-for="team-select-tooltip"
                          onClick={() => onToggleSubscription({ teamId, teamCode, isSubscribed })}
                          size="large"
                        >
                          {isSubscribed
                            ? <SubscriptionsIcon />
                            : <SubscriptionsNoneIcon />}
                        </IconButton>
                        <IconButton
                          sx={{ padding: '10px' }}
                          edge="end"
                          aria-label="Toggle Notification"
                          className={`${ActionButtonClass} ${isNotify ? 'active' : ''}`}
                          data-tip={`${isNotify ? 'Remove' : 'Add'} Notification`}
                          data-for="team-select-tooltip"
                          onClick={() => onToggleNotification({ teamId, teamCode, isNotify })}
                          size="large"
                        >
                          {isNotify
                            ? <NotificationsIcon />
                            : <NotificationsNoneIcon />}
                        </IconButton>
                      </>
                    )}
                    disablePadding
                  >
                    <ListItemButton onClick={() => onTeamSelectionToggle(conferenceName, teamId)}>
                      <TeamCheckbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ImageContainer>
                        <Image src={teamImageFile} />
                      </ImageContainer>
                      <ListItemText primaryTypographyProps={{ paddingRight: '24px' }} primary={teamName} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
            <ReactTooltip
              id="team-select-tooltip"
              delayShow={250}
              effect="solid"
            />
          </Fragment>
        ))}
      </TeamsList>
    );
  };

  const renderTeamFilter = () => {
    const handleTextFilter = (filter: string) => {
      if (filter !== filterText) {
        setFilterText(
          filter,
        );
        if (filter.length >= 3 && !allExpanded) {
          setAllExpanded(true);
          onConferenceToggleAll(true);
        }
        if (filter.length < 3 && allExpanded) {
          setAllExpanded(false);
          onConferenceToggleAll(false);
        }
      }
    };

    return (
      <TextField
        autoFocus
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="Search Teams"
        variant="outlined"
        fullWidth
        onChange={(e) => handleTextFilter(e.target.value)}
        value={filterText}
        InputProps={{
          'aria-label': 'bare',
          style: {
            width: '100%', height: 0.2, fontSize: '14px', padding: '20px', position: 'sticky',
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
          inputProps: {
            style: { padding: '0px' },
          },
        }}
      />
    );
  };

  return (
    <Container>
      <ControlLabel>
        Team(s):
      </ControlLabel>
      <Button
        variant="plainOnPaper"
        onClick={openTeamSelection}
        data-auto="SelectTeamsBtn"
      >
        Select Teams
      </Button>
      <Button
        variant="plainOnPaper"
        onClick={onTeamSelectionClear}
        data-auto="ClearTeamsBtn"
      >
        Clear Teams
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeTeamSelection}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <Header
          data-auto="SelectTeamsModal"
        >
          {renderTeamFilter()}
        </Header>
        {renderTeamOptions()}
      </Popover>
    </Container>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  conferences: state.exchange.searchParams.conferences,
  isLoading: state.exchange.notificationSettings.isLoading,
  subscription: state.exchange.notificationSettings.subscription,
  isUserExchangeVendor: selectIsUserExchangeVendor(state),
  filterText: selectTeamFilter(state),
  sport: state.exchange.initExchange.sport,
});

const mapDispatchToProps: IDispatchProps = {
  onQueryNotificationSettings: queryNotificationSettings,
  onConferenceToggle: toggleConferenceExpandedState,
  onConferenceToggleAll: toggleConferenceAllExpandedState,
  onAllTeamsSelection: toggleAllTeamsSelection,
  onTeamSelectionToggle: toggleTeamSelection,
  onTeamSelectionClear: clearTeamSelection,
  onUpdateSettings: updateSettings,
  onSaveSettings: saveSettings,
  setFilterText: updateTeamFilter,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExchangeSearchParams);
